/**
 * Budget Pivot — user-customisable pivot over the position-month fact base
 * (GET /budget/pivot): pick row dimensions (max 3), optionally spread months
 * across columns, choose rand/headcount measures, multi-select filters, month
 * scope (YTD-finalised / full year / single month) and occupancy (live / all /
 * retired-only, so abolished seats can be isolated or excluded).
 *
 * The "ACATEA envelopes" preset is the per-dimension management view Finance
 * asked for: campus × faculty × employment class under ACATEA, budget vs
 * actual vs headcount per envelope.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Loader2, Download, RefreshCw, ChevronDown, Layers } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  budgetService, BudgetFilterOptions, BudgetPivotDimension, BudgetPivotMeasure,
  BudgetPivotResponse,
} from '@/services/api/organizationService';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DIM_OPTIONS: { key: BudgetPivotDimension; label: string }[] = [
  { key: 'campus', label: 'Campus' },
  { key: 'department', label: 'Department (Financial)' },
  { key: 'faculty', label: 'Faculty' },
  { key: 'function', label: 'Function' },
  { key: 'rm_department', label: 'Department (Org)' },
  { key: 'team_1', label: 'Team 1' },
  { key: 'team_2', label: 'Team 2' },
  { key: 'team_3', label: 'Team 3' },
  { key: 'employment', label: 'Employment type' },
  { key: 'job_title', label: 'Job title' },
];
const DIM_LABEL = Object.fromEntries(DIM_OPTIONS.map(d => [d.key, d.label]));

const MEASURE_OPTIONS: { key: BudgetPivotMeasure; label: string; rand?: boolean }[] = [
  { key: 'budgeted', label: 'Budgeted', rand: true },
  { key: 'actual', label: 'Actual', rand: true },
  { key: 'variance', label: 'Variance', rand: true },
  { key: 'bonus', label: 'Bonus/Comm', rand: true },
  { key: 'actual_excl_bonus', label: 'Actual (excl. bonus/comm)', rand: true },
  { key: 'cc', label: 'Employer contrib.', rand: true },
  { key: 'actual_ctc', label: 'Actual (CTC)', rand: true },
  { key: 'variance_ctc', label: 'Variance (CTC)', rand: true },
  { key: 'positions', label: 'Positions' },
  { key: 'filled', label: 'Filled' },
  { key: 'vacant', label: 'Vacant' },
  { key: 'budgeted_headcount', label: 'Budgeted HC' },
];
const MEASURE_LABEL = Object.fromEntries(MEASURE_OPTIONS.map(m => [m.key, m.label]));
const RAND_MEASURES = new Set(MEASURE_OPTIONS.filter(m => m.rand).map(m => m.key));

const fmtRand = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `${n < 0 ? '−' : ''}R${(a / 1e6).toFixed(1)}m`;
  if (a >= 1e3) return `${n < 0 ? '−' : ''}R${Math.round(a / 1e3)}k`;
  return `${n < 0 ? '−' : ''}R${Math.round(a)}`;
};
const fmtCell = (measure: string, v: number) =>
  RAND_MEASURES.has(measure as BudgetPivotMeasure) ? fmtRand(v) : v.toLocaleString();

const SENTINELS: Record<string, string> = {
  __UNASSIGNED__: '(unassigned)', __VACANT__: '(vacant)', __UNKNOWN__: '(unknown)',
};
const dimVal = (v: string | number) => SENTINELS[String(v)] ?? String(v);

/** Compact multi-select dropdown (checkbox list). When `onModeChange` is
 *  supplied the dropdown offers Include/Exclude — Exclude means "everything
 *  except the ticked values" (e.g. Department ≠ ACATEA in one click). */
function MultiPick({ label, options, selected, onChange, max, mode, onModeChange }: {
  label: string; options: string[]; selected: string[];
  onChange: (next: string[]) => void; max?: number;
  mode?: 'include' | 'exclude'; onModeChange?: (m: 'include' | 'exclude') => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const toggle = (v: string) => {
    if (selected.includes(v)) onChange(selected.filter(x => x !== v));
    else if (!max || selected.length < max) onChange([...selected, v]);
  };
  // Search box for high-cardinality axes (job titles). Selected values always
  // stay visible above the matches so a filter can't hide what's active.
  const searchable = options.length > 15;
  const visible = searchable && search
    ? options.filter(v => v.toLowerCase().includes(search.toLowerCase()) || selected.includes(v))
    : options;
  const excluding = mode === 'exclude';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs',
          selected.length
            ? (excluding ? 'border-rose-400/60 bg-rose-50 text-rose-700' : 'border-heritage-blue/50 bg-heritage-blue/5 text-heritage-blue')
            : 'text-muted-foreground')}>
        {excluding && selected.length ? 'NOT ' : ''}{label}{selected.length ? ` (${selected.length})` : ''}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border bg-white p-1 shadow-lg">
          {onModeChange && (
            <div className="mb-1 flex rounded border text-[10px]">
              <button type="button" onClick={() => onModeChange('include')}
                className={cn('flex-1 px-1.5 py-1', !excluding ? 'bg-heritage-blue text-white' : 'text-muted-foreground hover:bg-muted')}>
                Include
              </button>
              <button type="button" onClick={() => onModeChange('exclude')}
                title="Everything EXCEPT the ticked values"
                className={cn('flex-1 px-1.5 py-1', excluding ? 'bg-rose-600 text-white' : 'text-muted-foreground hover:bg-muted')}>
                Exclude
              </button>
            </div>
          )}
          {searchable && (
            <input
              type="text" value={search} autoFocus
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${options.length} values…`}
              className="mb-1 w-full rounded border px-2 py-1 text-xs"
            />
          )}
          {visible.map(v => (
            <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted">
              <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
              <span className={cn('truncate', excluding && selected.includes(v) && 'text-rose-700 line-through')}>{v}</span>
            </label>
          ))}
          {!options.length && <div className="px-2 py-1 text-xs text-muted-foreground">No values</div>}
          {options.length > 0 && !visible.length && (
            <div className="px-2 py-1 text-xs text-muted-foreground">No matches for “{search}”</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BudgetPivot({ year }: { year: number }) {
  const [rows, setRows] = useState<BudgetPivotDimension[]>(['campus', 'faculty', 'employment']);
  const [monthsAsColumns, setMonthsAsColumns] = useState(false);
  // CTC-basis defaults — like-for-like against the RF1 budget (gross Actual
  // stays available in the measure picker for payroll-tie-out views).
  const [measures, setMeasures] = useState<BudgetPivotMeasure[]>(['budgeted', 'actual_ctc', 'variance_ctc', 'filled', 'vacant']);
  const [columnMeasure, setColumnMeasure] = useState<BudgetPivotMeasure>('actual_ctc');
  const [monthScope, setMonthScope] = useState<string>('ytd_finalised');
  const [occupancy, setOccupancy] = useState<'live' | 'all' | 'retired_only'>('live');
  const [filters, setFilters] = useState<Partial<Record<BudgetPivotDimension, string[]>>>({});
  const [excludeAxes, setExcludeAxes] = useState<Partial<Record<BudgetPivotDimension, boolean>>>({});
  const [options, setOptions] = useState<BudgetFilterOptions | null>(null);
  const [data, setData] = useState<BudgetPivotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDesc, setSortDesc] = useState(true);

  useEffect(() => {
    budgetService.getBudgetFilterOptions().then(setOptions).catch(() => setOptions(null));
  }, []);

  const effectiveMeasures = monthsAsColumns ? [columnMeasure] : measures;

  useEffect(() => {
    if (!rows.length || !effectiveMeasures.length) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    budgetService.getBudgetPivot({
      year,
      group_by: monthsAsColumns ? [...rows, 'month'] : rows,
      measures: effectiveMeasures,
      month_scope: monthScope,
      occupancy,
      filters,
      exclude: excludeAxes,
    }).then(r => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setError('Failed to load pivot'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, rows.join(','), monthsAsColumns, effectiveMeasures.join(','), monthScope, occupancy, JSON.stringify(filters), JSON.stringify(excludeAxes)]);

  const applyAcateaPreset = () => {
    setRows(['campus', 'faculty', 'employment']);
    setMonthsAsColumns(false);
    setMeasures(['budgeted', 'actual_ctc', 'variance_ctc', 'positions', 'filled', 'vacant']);
    setMonthScope('ytd_finalised');
    setOccupancy('live');
    setFilters({ department: ['ACATEA'] });
    setExcludeAxes({});
  };

  // ── shape rows for render ──
  const { tableRows, monthCols } = useMemo(() => {
    if (!data) return { tableRows: [] as Record<string, string | number>[], monthCols: [] as number[] };
    if (!monthsAsColumns) {
      const rs = [...data.rows];
      if (sortKey) {
        rs.sort((a, b) => {
          const av = a[sortKey], bv = b[sortKey];
          const cmp = typeof av === 'number' && typeof bv === 'number'
            ? av - bv : String(av).localeCompare(String(bv));
          return sortDesc ? -cmp : cmp;
        });
      }
      return { tableRows: rs, monthCols: [] };
    }
    // wide: one row per dim-tuple, one column per month (single measure)
    const byKey = new Map<string, Record<string, string | number>>();
    const seenMonths = new Set<number>();
    for (const r of data.rows) {
      const key = rows.map(d => String(r[d])).join('|');
      const m = Number(r.month);
      seenMonths.add(m);
      let row = byKey.get(key);
      if (!row) {
        row = Object.fromEntries(rows.map(d => [d, r[d]]));
        byKey.set(key, row);
      }
      row[`m${m}`] = Number(r[columnMeasure] ?? 0);
    }
    const months = [...seenMonths].sort((a, b) => a - b);
    const rs = [...byKey.values()];
    rs.forEach(row => { row.__total = months.reduce((s, m) => s + Number(row[`m${m}`] ?? 0), 0); });
    rs.sort((a, b) => Number(b.__total) - Number(a.__total));
    return { tableRows: rs, monthCols: months };
  }, [data, monthsAsColumns, rows, columnMeasure, sortKey, sortDesc]);

  const { totals, monthTotals, grandTotal } = useMemo(() => {
    const t: Record<string, number> = {};
    for (const m of effectiveMeasures) t[m] = tableRows.reduce((s, r) => s + Number(r[m] ?? 0), 0);
    const mt: Record<number, number> = {};
    let gt = 0;
    if (monthsAsColumns) {
      for (const m of monthCols) mt[m] = tableRows.reduce((s, r) => s + Number(r[`m${m}`] ?? 0), 0);
      gt = tableRows.reduce((s, r) => s + Number(r.__total ?? 0), 0);
    }
    return { totals: t, monthTotals: mt, grandTotal: gt };
  }, [tableRows, effectiveMeasures, monthsAsColumns, monthCols]);

  const downloadCsv = () => {
    if (!data) return;
    const dimCols = rows.map(d => DIM_LABEL[d]);
    let header: string[]; let lines: string[];
    if (monthsAsColumns) {
      header = [...dimCols, ...monthCols.map(m => MONTHS[m - 1]), 'Total'];
      lines = tableRows.map(r => [
        ...rows.map(d => dimVal(r[d])),
        ...monthCols.map(m => String(r[`m${m}`] ?? 0)),
        String(r.__total ?? 0),
      ].join(','));
    } else {
      header = [...dimCols, ...effectiveMeasures.map(m => MEASURE_LABEL[m])];
      lines = tableRows.map(r => [
        ...rows.map(d => dimVal(r[d])),
        ...effectiveMeasures.map(m => String(r[m] ?? 0)),
      ].join(','));
    }
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `budget_pivot_${year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Position-level XLSX behind the current view — one row per seat with incumbent,
  // year assignment history and per-month Budget/Actual/Bonus lanes, so any pivot
  // cell can be footed to named employees.
  const [exportingDetail, setExportingDetail] = useState(false);
  const downloadDetail = async () => {
    setExportingDetail(true);
    try {
      const blob = await budgetService.exportBudgetPivotDetail({
        year, month_scope: monthScope, occupancy, filters, exclude: excludeAxes,
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `budget_pivot_detail_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExportingDetail(false);
    }
  };

  const finalised = data?.actuals_finalised_through ?? 0;
  const provisional = data?.provisional_months ?? [];

  return (
    <div className="space-y-3">
      {/* Controls */}
      <Card>
        <CardContent className="space-y-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Rows</span>
            <MultiPick label="Dimensions" max={3}
              options={DIM_OPTIONS.map(d => d.label)}
              selected={rows.map(d => DIM_LABEL[d])}
              onChange={labels => setRows(DIM_OPTIONS.filter(d => labels.includes(d.label)).map(d => d.key))} />
            {rows.map(d => (
              <span key={d} className="rounded-full bg-heritage-blue/10 px-2 py-0.5 text-xs text-heritage-blue">
                {DIM_LABEL[d]}
              </span>
            ))}
            <label className="ml-2 inline-flex cursor-pointer items-center gap-1.5 text-xs">
              <input type="checkbox" checked={monthsAsColumns} onChange={e => setMonthsAsColumns(e.target.checked)} />
              Months across columns
            </label>
            {monthsAsColumns ? (
              <Select value={columnMeasure} onValueChange={v => setColumnMeasure(v as BudgetPivotMeasure)}>
                <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEASURE_OPTIONS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <MultiPick label="Measures"
                options={MEASURE_OPTIONS.map(m => m.label)}
                selected={measures.map(m => MEASURE_LABEL[m])}
                onChange={labels => setMeasures(MEASURE_OPTIONS.filter(m => labels.includes(m.label)).map(m => m.key))} />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Scope</span>
            <Select value={monthScope} onValueChange={setMonthScope}>
              <SelectTrigger className="h-7 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ytd_finalised">YTD (finalised months)</SelectItem>
                <SelectItem value="full_year">Full year</SelectItem>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m} only</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={occupancy} onValueChange={v => setOccupancy(v as typeof occupancy)}>
              <SelectTrigger className="h-7 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live seats (excl. abolished)</SelectItem>
                <SelectItem value="all">All seats (incl. abolished)</SelectItem>
                <SelectItem value="retired_only">Abolished only</SelectItem>
              </SelectContent>
            </Select>
            <span className="ml-2 text-xs font-semibold text-muted-foreground">Filters</span>
            {DIM_OPTIONS.map(d => (
              <MultiPick key={d.key} label={d.label}
                options={(options?.[d.key === 'employment' ? 'employment' : d.key] as string[] | undefined)
                  ?? []}
                selected={filters[d.key] ?? []}
                onChange={vals => setFilters(f => ({ ...f, [d.key]: vals.length ? vals : undefined }))}
                mode={excludeAxes[d.key] ? 'exclude' : 'include'}
                onModeChange={m => setExcludeAxes(e => ({ ...e, [d.key]: m === 'exclude' || undefined }))} />
            ))}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={applyAcateaPreset} title="Campus × Faculty × Employment class under ACATEA — the per-dimension envelope view">
                <Layers className="mr-1 h-3.5 w-3.5" /> ACATEA envelopes
              </Button>
              <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!tableRows.length}>
                <Download className="mr-1 h-3.5 w-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={downloadDetail} disabled={exportingDetail}
                title="Position-level XLSX of the seats behind this view — incumbent, year assignment history, per-month Budget/Actual/Bonus">
                <Download className="mr-1 h-3.5 w-3.5" /> {exportingDetail ? 'Exporting…' : 'Detail XLSX'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boundary note */}
      {monthScope === 'ytd_finalised' && finalised > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          Actuals finalised through <b>{MONTHS[finalised - 1]}</b>
          {provisional.length > 0 && (
            <> — {provisional.map(m => MONTHS[m - 1]).join(', ')} posted but not finalised (excluded here)</>
          )}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading pivot…
            </div>
          ) : error ? (
            <div className="flex items-center justify-between px-4 py-6 text-sm text-red-600">
              {error}
              <Button variant="outline" size="sm" onClick={() => setMonthScope(s => s)}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry
              </Button>
            </div>
          ) : !rows.length ? (
            /* No dimension selected: the backend requires ≥1 group_by, so the
               last-fetched table would be stale AND the TOTAL row misaligns
               (colSpan=0 spans-to-end per the HTML spec). Show a hint instead. */
            <div className="py-10 text-center text-sm text-muted-foreground">
              Select at least one dimension under <b>Rows</b> to build the pivot.
            </div>
          ) : (
            <div className="max-h-[62vh] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-800 text-white">
                  <tr>
                    {rows.map(d => (
                      <th key={d} className="px-2 py-1.5 text-left font-medium">{DIM_LABEL[d]}</th>
                    ))}
                    {monthsAsColumns ? (
                      <>
                        {monthCols.map(m => (
                          <th key={m} className={cn('px-2 py-1.5 text-right font-medium tabular-nums',
                            provisional.includes(m) && 'text-amber-300')}>
                            {MONTHS[m - 1]}{provisional.includes(m) ? '*' : ''}
                          </th>
                        ))}
                        <th className="px-2 py-1.5 text-right font-semibold">Total</th>
                      </>
                    ) : (
                      effectiveMeasures.map(m => (
                        <th key={m}
                          className="cursor-pointer px-2 py-1.5 text-right font-medium tabular-nums hover:underline"
                          onClick={() => { setSortKey(m); setSortDesc(k => (sortKey === m ? !k : true)); }}>
                          {MEASURE_LABEL[m]}{sortKey === m ? (sortDesc ? ' ↓' : ' ↑') : ''}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                      {rows.map(d => (
                        <td key={d} className="px-2 py-1">{dimVal(r[d])}</td>
                      ))}
                      {monthsAsColumns ? (
                        <>
                          {monthCols.map(m => (
                            <td key={m} className={cn('px-2 py-1 text-right tabular-nums',
                              provisional.includes(m) && 'text-amber-600/70')}>
                              {fmtCell(columnMeasure, Number(r[`m${m}`] ?? 0))}
                            </td>
                          ))}
                          <td className="px-2 py-1 text-right font-semibold tabular-nums">
                            {fmtCell(columnMeasure, Number(r.__total ?? 0))}
                          </td>
                        </>
                      ) : (
                        effectiveMeasures.map(m => (
                          <td key={m} className={cn('px-2 py-1 text-right tabular-nums',
                            m === 'variance' && Number(r[m]) < 0 && 'text-red-600',
                            m === 'variance' && Number(r[m]) > 0 && 'text-emerald-700')}>
                            {fmtCell(m, Number(r[m] ?? 0))}
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
                {tableRows.length > 1 && (
                  /* sticky must sit on the CELLS: Chrome ignores position:sticky
                     on tfoot/tr, so each td pins itself to the scroll bottom. */
                  <tfoot className="font-semibold">
                    <tr>
                      <td className="sticky bottom-0 border-t bg-slate-100 px-2 py-1.5" colSpan={Math.max(rows.length, 1)}>
                        TOTAL · {tableRows.length} rows{data?.truncated ? ' (truncated)' : ''}
                      </td>
                      {monthsAsColumns ? (
                        <>
                          {monthCols.map(m => (
                            <td key={m} className={cn('sticky bottom-0 border-t bg-slate-100 px-2 py-1.5 text-right tabular-nums',
                              provisional.includes(m) && 'text-amber-600/70')}>
                              {fmtCell(columnMeasure, monthTotals[m] ?? 0)}
                            </td>
                          ))}
                          <td className="sticky bottom-0 border-t bg-slate-100 px-2 py-1.5 text-right tabular-nums">
                            {fmtCell(columnMeasure, grandTotal)}
                          </td>
                        </>
                      ) : (
                        effectiveMeasures.map(m => (
                          <td key={m} className="sticky bottom-0 border-t bg-slate-100 px-2 py-1.5 text-right tabular-nums">
                            {fmtCell(m, totals[m] ?? 0)}
                          </td>
                        ))
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
              {!tableRows.length && (
                <div className="py-10 text-center text-sm text-muted-foreground">No rows for this view.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
